import { useMutation, useQuery } from '@vue/apollo-composable';
import { useStorage } from '@vueuse/core';
import gql from 'graphql-tag';
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import dayjs from '@/dayjs';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';
import { useCompanyStore } from './company';

export const useTreatwellStore = defineStore('treatwell', () => {
  const { hasFeatureFlag } = useUserStore();
  const { isTreatwellUser, company } = storeToRefs(useCompanyStore());

  const treatwellKycFetchError = ref(false);

  const treatwellStatus = reactive({
    bankDetailsProvided: false,
    contractSigned: false,
    contractType: null,
    contractId: null,
    descriptionProvided: false,
    freeCommissionRemainingDays: 0,
    isPastFreeCommissionPeriod: false,
    subscriptionIntention: false,
    hasRequest: false,
    hasVenue: false,
    hasPhotos: false,
    venuePending: true,
    optOutCampaign: false,
    reviewsPublished: false,
    twHardNo: false,
    dac7Detail: false,
    kycStatus: {
      onboardingUrl: '',
      kycCompleted: false,
    },
  });

  const isExcluded = computed(
    () => company.value.country === 'es' && !treatwellStatus.hasRequest,
  );

  const showTreatwell = computed(
    () =>
      hasFeatureFlag('module-treatwell') &&
      !treatwellStatus.twHardNo &&
      !isExcluded.value,
  );

  const hasKyc = computed(
    () =>
      unleash.isEnabled('TW_KYC_FLOW') &&
      showTreatwell.value &&
      treatwellStatus.hasVenue,
  );

  const showTreatwellOfferBanner = computed(
    () =>
      showTreatwell.value &&
      !treatwellStatus.hasVenue &&
      company.value.activeSubscription?.plan !== 'lite' &&
      unleash.isEnabled('ShowTreatwellMarketplaceOffer'),
  );

  const hasDashboard = computed(
    () =>
      treatwellStatus.contractSigned ||
      treatwellStatus.optOutCampaign ||
      treatwellStatus.hasRequest,
  );

  const completedOnboardingTasks = computed(() => {
    if (isTreatwellUser.value) {
      return treatwellStatus.kycStatus?.kycCompleted;
    } else {
      return (
        treatwellStatus.contractSigned &&
        providedCompanyInfo.value &&
        treatwellStatus.bankDetailsProvided &&
        treatwellStatus.kycStatus?.kycCompleted
      );
    }
  });

  const showTWOnboardingNotification = computed(() => {
    return (
      hasDashboard.value &&
      !completedOnboardingTasks.value &&
      treatwellStatus.freeCommissionRemainingDays <= 14
    );
  });

  const providedCompanyInfo = computed(
    () =>
      !!company.value.address &&
      !!company.value.city &&
      !!company.value.postalcode &&
      !!company.value.phone &&
      treatwellStatus.dac7Detail,
  );

  const onlyMissingCompanyInfo = computed(() => {
    const otherTasksDone = isTreatwellUser.value
      ? !!treatwellStatus.kycStatus?.kycCompleted
      : treatwellStatus.contractSigned &&
        treatwellStatus.bankDetailsProvided &&
        !!treatwellStatus.kycStatus?.kycCompleted;

    return hasDashboard.value && otherTasksDone && !providedCompanyInfo.value;
  });

  const fetchTreatwellStatus = () => {
    // Query for the main status

    return new Promise<void>((resolve, reject) => {
      const { onResult, onError } = useQuery(gql`
        query getTreatwellStatus {
          treatwellStatus {
            bankDetailsProvided
            contractSigned
            contractType
            contractId
            descriptionProvided
            freeCommissionRemainingDays
            subscriptionIntention
            hasVenue
            hasPhotos
            venuePending
            hasRequest
            optOutCampaign
            reviewsPublished
            twHardNo
            isPastFreeCommissionPeriod
            dac7Detail
          }
        }
      `);

      onResult(({ data: { treatwellStatus: _treatwellStatus } }) => {
        if (!_treatwellStatus) {
          resolve();
          // biome-ignore lint/suspicious/noConsole: Needed for debugging
          console.warn('Treatwell status query returned null data.');
          return;
        }

        // Define keys for direct mapping
        const directMappingKeys: (keyof Omit<
          typeof treatwellStatus,
          'kycStatus'
        >)[] = [
          'bankDetailsProvided',
          'contractSigned',
          'contractType',
          'contractId',
          'descriptionProvided',
          'freeCommissionRemainingDays',
          'isPastFreeCommissionPeriod',
          'subscriptionIntention',
          'hasRequest',
          'hasVenue',
          'venuePending',
          'optOutCampaign',
          'reviewsPublished',
          'twHardNo',
          'dac7Detail',
        ];

        // Loop through direct mapping keys and update state if defined
        directMappingKeys.forEach((key) => {
          if (_treatwellStatus[key] !== undefined) {
            // Type assertion needed here because TS struggles with indexed access on unions
            (treatwellStatus as any)[key] = _treatwellStatus[key];
          }
        });

        resolve();
      });

      onError(() => {
        reject();
      });
    });
  };

  const fetchKycStatus = () => {
    // Second, non-blocking query for KYC status
    return new Promise<void>((resolve, reject) => {
      const { onResult, onError } = useQuery(
        gql`
          query getTreatwellKycStatus {
            treatwellKycStatus {
              kycCompleted
              onboardingUrl
            }
          }
        `,
      );

      onResult(({ data }) => {
        const _kycStatus = data?.treatwellKycStatus;
        if (!_kycStatus) {
          resolve();
          return; // Can be null, not an error
        }

        if (_kycStatus.onboardingUrl !== undefined) {
          treatwellStatus.kycStatus.onboardingUrl = _kycStatus.onboardingUrl;
        }
        if (_kycStatus.kycCompleted !== undefined) {
          treatwellStatus.kycStatus.kycCompleted = _kycStatus.kycCompleted;
        }

        resolve();

        // Keep the mutation logic as is, triggered after KYC status update
        if (treatwellStatus.kycStatus) {
          const { mutate } = useMutation(
            gql`
              mutation treatwellUpdateKycStatus($input: UpdateKycStatusInput!) {
                treatwellUpdateKycStatus(input: $input) {
                  errors {
                    attribute
                    message
                  }
                }
              }
            `,
            {
              variables: {
                input: {
                  kycCompleted: treatwellStatus.kycStatus.kycCompleted,
                },
              },
            },
          );
          mutate();
        }
      });

      onError(() => {
        treatwellKycFetchError.value = true;
        reject();
      });
    });
  };

  // `hasKyc` depends on a feature flag, so callers that run this while the
  // flags are still loading (app boot) pass the promise that resolves once
  // they are known. Deciding on the KYC fetch before that raced the Unleash
  // request and randomly skipped it, which left the KYC modals hidden.
  //
  // Every caller of this fires it fire-and-forget (no .catch), and at least
  // one now awaits it before finishing its own success path (see
  // SalonAddressModal.vue) - so this must always eventually resolve, never
  // hang and never reject, regardless of what the underlying queries do.
  const getTreatwellStatus = (
    flagsReady: Promise<unknown> = Promise.resolve(),
  ): Promise<void> =>
    Promise.all([
      // Failures aren't surfaced anywhere for this fetch specifically (unlike
      // fetchKycStatus, which sets treatwellKycFetchError) - swallowed here
      // for now rather than left to hang the caller forever.
      fetchTreatwellStatus().catch(() => undefined),
      flagsReady,
    ]).then(async () => {
      if (hasKyc.value) {
        // Failures surface through treatwellKycFetchError; never block the caller
        await fetchKycStatus().catch(() => undefined);
      }
    });

  const needsToCompleteKYC = computed(
    () =>
      hasKyc.value &&
      treatwellStatus.kycStatus?.onboardingUrl &&
      !treatwellStatus.kycStatus?.kycCompleted,
  );

  const hasActiveVenue = computed(
    () => treatwellStatus.hasVenue && !treatwellStatus.venuePending,
  );

  // Path blocking logic for KYC modals
  const pathBlocked = computed<boolean>(() => {
    const route = useRoute();

    if (route.name === 'treatwell-admin') {
      return true;
    }

    // Do not show the popup when the path includes any of the excludePaths keywords
    const path = route.path.split('/');
    const excludePaths = [
      'new',
      'customers',
      'create-event',
      'checkout',
      'sales',
    ];
    let pathExcluded = false;
    path.forEach((subPath: string) => {
      if (excludePaths.includes(subPath)) {
        pathExcluded = true;
      }
    });
    return pathExcluded;
  });

  // KYC modal logic
  const kycModalDismissedAt = useStorage('tw_kyc_modal_dismissed_at', 0);

  const kycUtimatumActive = computed(
    () =>
      needsToCompleteKYC.value &&
      unleash.isEnabled('TW_KYC_ULTIMATUM') &&
      !treatwellStatus.venuePending &&
      treatwellStatus.freeCommissionRemainingDays > 0 &&
      treatwellStatus.freeCommissionRemainingDays < 15,
  );

  const shouldShowKYCUltimatum = computed(
    () =>
      kycUtimatumActive.value &&
      dayjs().diff(kycModalDismissedAt.value, 'day', true) > 3 &&
      !pathBlocked.value,
  );

  const dismissKYCModal = () => {
    kycModalDismissedAt.value = Date.now();
  };

  const dismissTreatwellKycFetchError = () => {
    treatwellKycFetchError.value = false;
  };

  return {
    getTreatwellStatus,
    treatwellStatus,
    showTreatwell,
    hasDashboard,
    completedOnboardingTasks,
    providedCompanyInfo,
    onlyMissingCompanyInfo,
    needsToCompleteKYC,
    kycUtimatumActive,
    shouldShowKYCUltimatum,
    dismissKYCModal,
    hasActiveVenue,
    showTWOnboardingNotification,
    showTreatwellOfferBanner,
    treatwellKycFetchError,
    dismissTreatwellKycFetchError,
  };
});
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTreatwellStore, import.meta.hot));
}
